import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, LogOutIcon, PlusIcon, RotateCcwIcon, SettingsIcon, UserIcon, UsersIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { useUi } from '../../contexts/UiContext';
import { Avatar } from '../Avatar';
interface AvatarMenuProps {
  placement: 'down' | 'up';
  showName?: boolean;
}
const itemCls = 'flex h-11 w-full items-center gap-3 px-4 text-left text-sm font-semibold transition-colors duration-150 hover:bg-canvas';
export function AvatarMenu({
  placement,
  showName
}: AvatarMenuProps) {
  const {
    viewer,
    isAdmin,
    family,
    myFamilies,
    switchFamily,
    resetPrototype,
    changePerson
  } = useAppState();
  const {
    openSub
  } = useUi();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const go = (fn: () => void) => {
    setOpen(false);
    fn();
  };
  return <div ref={ref} className="relative" data-id="element-857" data-mp="2uzi4bdvcy5f8">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-haspopup="menu" aria-label={`Meni: ${viewer.name}`} className={`flex items-center gap-2 transition-colors duration-150 hover:bg-canvas focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent ${showName ? 'w-full rounded-2xl p-2' : 'rounded-full p-1'}`} data-id="element-858" data-mp="2uyfb2zakbqw5">
        <Avatar member={viewer} data-id="element-859" data-mp="2c2p756j2kkfm" />
        {showName && <>
            <span className="min-w-0 flex-1 text-left" data-id="element-860" data-mp="22bfcx88reuu1">
              <span className="block truncate text-sm font-extrabold" data-id="element-861" data-mp="27cmjj3z61nz4">{viewer.name}</span>
              <span className="block text-xs text-muted" data-id="element-862" data-mp="1tclinoiwigjn">{isAdmin ? 'Skrbnik' : 'Otrok'}</span>
            </span>
            <ChevronDownIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-863" data-mp="1xddu8of8wxj3" />
          </>}
      </button>
      <AnimatePresence data-id="element-864" data-mp="2l4j84y1mlfqr">
        {open && <motion.div role="menu" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className={`absolute z-40 w-72 overflow-hidden rounded-2xl border border-line bg-surface py-1 shadow-bar ${placement === 'down' ? 'right-0 top-full mt-2 origin-top-right' : 'bottom-full left-0 mb-2 origin-bottom-left'}`} data-id="element-865" data-mp="2l5ubwxv2mnhv">
            <div className="border-b border-line px-4 pb-2 pt-3" data-id="element-866" data-mp="xdlfcuz08792">
              <p className="text-xs font-bold text-muted" data-id="element-867" data-mp="xbqufgnqgf4z">Moje družine</p>
              <ul className="mt-1" data-id="element-868" data-mp="seh664c2y9iq">
                {myFamilies.map(f => <li key={f.id} data-id="element-869" data-mp="172vc4o4sme3i">
                    <button role="menuitemradio" aria-checked={f.id === family.id} type="button" onClick={() => go(() => switchFamily(f.id))} className="flex h-10 w-full items-center gap-2 rounded-xl text-left text-sm font-bold hover:bg-canvas" data-id="element-870" data-mp="175zjfudk1pye">
                      <span className="grid h-5 w-5 place-items-center" data-id="element-871" data-mp="9gxxj1tjaswu">{f.id === family.id && <CheckIcon className="h-4 w-4 text-done" aria-hidden="true" data-id="element-872" data-mp="odf8hrxta5iv" />}</span>
                      Družina {f.name}
                    </button>
                  </li>)}
              </ul>
            </div>
            <button role="menuitem" type="button" onClick={() => go(changePerson)} className={itemCls} data-id="element-873" data-mp="1zrizqqd0lfk8">
              <UsersIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-874" data-mp="1usfw9aconhed" /> Zamenjaj osebo
            </button>
            <button role="menuitem" type="button" onClick={() => go(() => openSub({
          kind: 'profile'
        }))} className={itemCls} data-id="element-875" data-mp="4dyfd2zyhybq">
              <UserIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-876" data-mp="9dkvplb2zuor" /> Moj profil in vsebine
            </button>
            {isAdmin && <button role="menuitem" type="button" onClick={() => go(() => openSub({
          kind: 'settings'
        }))} className={itemCls} data-id="element-877" data-mp="icew7mrj957l">
                <SettingsIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-878" data-mp="naq4vu63d9o8" /> Nastavitve družine
              </button>}
            <button role="menuitem" type="button" onClick={() => go(() => openSub({
          kind: 'wizard'
        }))} className={itemCls} data-id="element-879" data-mp="x11arm9c5sxr">
              <PlusIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-880" data-mp="112wawnfj609y" /> Ustvari novo družino
            </button>
            <div className="mt-1 border-t border-line pt-1" data-id="element-881" data-mp="162sisxrcmbus">
              <button role="menuitem" type="button" onClick={() => go(resetPrototype)} className={itemCls} data-id="element-882" data-mp="1b0kjag6276al">
                <RotateCcwIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-883" data-mp="1l5ac9wjhj02o" /> Ponastavi vzorčne podatke
              </button>
              <button role="menuitem" type="button" disabled className={`${itemCls} text-muted hover:bg-transparent`} data-id="element-884" data-mp="1g4wfjanc3cjy">
                <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-885" data-mp="1b6uvame6976w" /> Odjava (v prototipu ni prijave)
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}